import './App.css'
import { useEffect, useState } from 'react';
import { Home } from './Components/landing/home';
import { Dashboard } from './Components/dashboard';
import { onAuthStateChanged } from 'firebase/auth';
import { Terms } from './Components/landing/terms';
import { auth, firestore } from './firebase-config';
import { About } from './Components/landing/product';
import { Header } from './Components/landing/header';
import { Footer } from './Components/landing/footer';
import { LoadingScreen } from './Components/widgets';
import { Privacy } from './Components/landing/privacy';
import { Pricing } from './Components/landing/payments';
import { PageNotFound } from './Components/pageNotFound';
import { AppMode, AuthStatus } from './Components/values';
import { ProfilePage } from './Components/landing/profile';
import { Onboarding } from './Components/landing/onboarding';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ForgotPassword, Login, Signup } from './Components/landing/auth';
import { BrowserRouter as Router, Switch, useLocation, Route, Redirect } from 'react-router-dom/cjs/react-router-dom'

function App() { return <Router><AppContent /></Router> }

function AppContent() {
  const location = useLocation();
  const getInitialAppMode = () => {
    const savedMode = localStorage.getItem('appMode');
    return savedMode ? JSON.parse(savedMode) : AppMode.LIGHT;
  };
  const [appMode, setAppMode] = useState(getInitialAppMode);
  const [userOnboardingStatus, setUserOnboardingStatus] = useState(null);
  const [authStatus, setAuthStatus] = useState(AuthStatus.NOT_DETERMINED);
  const [checkingOnboardingStatus, setCheckingOnboardingStatus] = useState(false);

  useEffect(() => {
    localStorage.setItem('appMode', JSON.stringify(appMode));
  }, [appMode]);

  useEffect(() => {
    const modifyAuthStatus = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthStatus(AuthStatus.LOGGED_IN);
        checkOnboardingStatus(user.uid);
      }
      else {
        setAuthStatus(AuthStatus.NOT_LOGGED_IN);
        setUserOnboardingStatus(null);
      }
    });
    return () => modifyAuthStatus();
  }, []);

  const checkOnboardingStatus = async (userId) => {
    setCheckingOnboardingStatus(true);
    try {
      const usersRef = collection(firestore, 'users');
      const q = query(usersRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setUserOnboardingStatus(userData.onboardingCompleted === true);
      } else {
        setUserOnboardingStatus(false);
      }
    } catch (error) {
      setUserOnboardingStatus(false);
    } finally {
      setCheckingOnboardingStatus(false);
    }
  };

  const toggleAppMode = () => {
    setAppMode((prevMode) => prevMode === AppMode.LIGHT ? AppMode.DARK : AppMode.LIGHT);
  };

  const updateOnboardingStatus = (status) => {
    setUserOnboardingStatus(status);
  };

  const refreshOnboardingStatus = async () => {
    if (!auth.currentUser) return;
    await checkOnboardingStatus(auth.currentUser.uid);
  };

  if (authStatus === AuthStatus.NOT_DETERMINED) { return <LoadingScreen appMode={appMode} />; }
  if (authStatus === AuthStatus.LOGGED_IN && checkingOnboardingStatus) {
    return <LoadingScreen appMode={appMode} />;
  }
  const hideHeaderFooterRoutes = ['/dashboard', '/login', '/signup', '/forgotPassword', '/onboard', '/user'];
  const shouldShowHeaderFooter = !hideHeaderFooterRoutes.includes(location.pathname);
  return (
    <>
      <ScrollToTop />
      {shouldShowHeaderFooter && <Header appMode={appMode} />}
      <Switch>
        <Route exact path="/" render={() => { return <Home /> }}></Route>
        <Route exact path="/terms" render={() => { return <Terms /> }}></Route>
        <Route exact path="/product" render={() => { return <About /> }}></Route>
        <Route exact path="/privacy" render={() => { return <Privacy /> }}></Route>
        <Route exact path="/pricing" render={() => { return <Pricing /> }}></Route>
        <Route exact path="/onboard" render={() => {
          if (authStatus !== AuthStatus.LOGGED_IN) {
            return <Redirect to='/login' />;
          }
          if (checkingOnboardingStatus) {
            return <LoadingScreen appMode={appMode} />;
          }
          if (userOnboardingStatus === true) {
            return <Redirect to='/dashboard' />;
          }
          return <Onboarding appMode={appMode} updateOnboardingStatus={updateOnboardingStatus} refreshOnboardingStatus={refreshOnboardingStatus} />;
        }} />
        <Route exact path="/forgotPassword" render={() => { return <ForgotPassword appMode={appMode} toggleAppMode={toggleAppMode} /> }}></Route>
        <Route exact path="/signup" render={() => { return <Signup appMode={appMode} setAuthStatus={setAuthStatus} toggleAppMode={toggleAppMode} /> }}></Route>
        <Route exact path="/user" render={() => {
          if (authStatus === AuthStatus.LOGGED_IN) { return <ProfilePage appMode={appMode} setAuthStatus={setAuthStatus} /> }
          else { return <Redirect to='/login' /> }
        }}></Route>
        <Route exact path="/login" render={() => {
          if (authStatus === AuthStatus.LOGGED_IN) {
            if (userOnboardingStatus === false) {
              return <Redirect to='/onboard' />;
            }
            return <Redirect to='/dashboard' />
          }
          else {
            return <Login appMode={appMode} setAuthStatus={setAuthStatus} toggleAppMode={toggleAppMode} />
          }
        }}></Route>
        <Route exact path="/dashboard" render={() => {
          if (authStatus === AuthStatus.LOGGED_IN) {
            if (checkingOnboardingStatus) {
              return <LoadingScreen appMode={appMode} />;
            }
            if (userOnboardingStatus === false) {
              return <Redirect to='/onboard' />;
            }
            return <Dashboard setAuthStatus={setAuthStatus} appMode={appMode} toggleAppMode={toggleAppMode} />;
          } else {
            return <Redirect to='/login' />;
          }
        }}></Route>
        <Route render={() => { return <PageNotFound appMode={appMode} /> }} />
      </Switch>
      {shouldShowHeaderFooter && <Footer appMode={appMode} />}
    </>
  );
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [pathname]);
  return null;
};

export default App;
